import type { BouquetBuilderItem, CustomerOrder, CustomerOrderLinePayload, PackageSize, PaperRollRow, OrderableItem, StaffOrderSource, OrderSource } from "../../api/types";
import { t } from "../../i18n/t";
import { compareText } from "../../i18n/intl";
import { fulfillmentLabel } from "./orderUi";
import type { PlainKey } from "../../i18n/types";

/**
 * Shared state shapes + pure helpers for the phone "Build & send order" flow
 * (OrderBuildPage → PhoneBouquetComposer → PhoneOrderCheckout).
 */

/** One flower in the phone cart. `maxQty` snapshots the location's stock cap. */
export interface ComposerFlowerLine {
  itemId: string;
  name: string;
  imageKey: string | null;
  qty: number;
  maxQty: number;
  /** Selling price per stem (typed, else derived from cost); null = no price and no cost. */
  unitSalePrice: number | null;
  unavailable?: boolean;
}

/** One ribbon/paper cut in the phone cart — an item + length, never a batch. */
export interface ComposerPaperLine {
  itemId: string;
  name: string;
  /** "Ribbon" | "Paper" — the category label shown on the row. */
  categoryLabel: string;
  cm: number;
  /** Total cm available across active batches at the location. */
  availableCm: number;
}

export interface ComposerCart {
  flowers: ComposerFlowerLine[];
  papers: ComposerPaperLine[];
  bouquetName: string;
  bouquetNotes: string;
  /**
   * G-15: agreed price of the BOUQUET as typed; empty = not quoted yet.
   * The order's goods total = this + the retail item lines. Delivery is
   * priced on the DeliveryJob and never folded in here (owner, 2026-07-31).
   */
  bouquetPriceEur: string;
}

export interface RetailOrderLine {
  itemId: string;
  name: string;
  imageKey: string | null;
  categoryId: string;
  categoryLabel: string;
  colorId: string | null;
  colorName: string | null;
  qty: number;
  maxQty: number;
  unitSalePrice: number | null;
  /** Preserved during editing when the original item is no longer in live stock. */
  unavailable?: boolean;
}

export const EMPTY_CART: ComposerCart = {
  flowers: [],
  papers: [],
  bouquetName: "",
  bouquetNotes: "",
  bouquetPriceEur: "",
};

export function cartIsEmpty(cart: ComposerCart): boolean {
  return cart.flowers.length === 0 && cart.papers.length === 0;
}

export function cartStemCount(cart: ComposerCart): number {
  return cart.flowers.reduce((sum, line) => sum + line.qty, 0);
}

/**
 * Suggested price = Σ qty × catalog selling price (flowers only — rolls carry
 * no selling price). Marks itself incomplete when any flower lacks a price,
 * mirroring the floor builder.
 */
export function cartSuggestedPrice(cart: ComposerCart): {
  totalEur: number;
  missingPrices: boolean;
} {
  let total = 0;
  let missing = false;
  for (const line of cart.flowers) {
    if (line.unitSalePrice == null) missing = true;
    else total += line.qty * line.unitSalePrice;
  }
  return { totalEur: total, missingPrices: missing };
}

/**
 * Serialize the cart into customer-order lines (Plan D2): flowers as
 * itemId+qty lines, ribbon/paper as itemId+cm lines. Description stays the
 * human-readable primary field everywhere orders render.
 */
export function cartToOrderLines(cart: ComposerCart): CustomerOrderLinePayload[] {
  const lines: CustomerOrderLinePayload[] = [];
  for (const f of cart.flowers) {
    lines.push({
      description: `${f.name} × ${f.qty}`,
      itemId: f.itemId,
      quantity: f.qty,
      unitPriceEur: f.unitSalePrice ?? undefined,
      role: "BOUQUET_COMPONENT",
    });
  }
  for (const p of cart.papers) {
    lines.push({
      // Stored text, built in the florist's language at the moment they
      // pick the length — S3's rule for a paper cut's label. Reuses the
      // builder's own template rather than restating it.
      description: t("builder.lengthPreset", { size: p.name, cm: p.cm }),
      itemId: p.itemId,
      quantity: p.cm,
      role: "BOUQUET_COMPONENT",
    });
  }
  return lines;
}

export function retailLineFromItem(item: OrderableItem): RetailOrderLine {
  return {
    itemId: item.id,
    name: item.name,
    imageKey: item.imageKey,
    categoryId: item.category.id,
    categoryLabel: item.category.label,
    colorId: item.color?.id ?? null,
    colorName: item.color?.name ?? null,
    qty: 1,
    maxQty: Number(item.qtyOnHand) || 0,
    // Typed price, else worked out from cost (G-45) — never the typed field
    // alone, or an automatically priced item reads as "price missing".
    unitSalePrice: item.saleUnitPriceEur != null ? Number(item.saleUnitPriceEur) : null,
    unavailable: false,
  };
}

export function retailToOrderLines(
  lines: RetailOrderLine[],
): CustomerOrderLinePayload[] {
  return lines.map((line) => ({
    description: line.name,
    itemId: line.itemId,
    quantity: line.qty,
    unitPriceEur: line.unitSalePrice ?? undefined,
    role: "RETAIL_ITEM",
  }));
}

export function retailTotal(lines: RetailOrderLine[]): number {
  return lines.reduce(
    (sum, line) => sum + (line.unitSalePrice ?? 0) * line.qty,
    0,
  );
}

/**
 * The order's GOODS total: agreed bouquet price + shop items (+ any
 * ready-made bouquets the order holds, G-112 — an edit keeps them). Delivery
 * is charged on the DeliveryJob (it also drives the courier payout), so it
 * is deliberately NOT part of the order total the register rings up.
 * Returns null when nothing is priced yet.
 */
export function goodsTotal(
  bouquetPriceEur: string,
  lines: RetailOrderLine[],
  heldBouquetsEur = 0,
): number | null {
  const bouquet = bouquetPriceEur.trim() ? Number(bouquetPriceEur) : null;
  const items = retailTotal(lines) + heldBouquetsEur;
  if (bouquet == null || !Number.isFinite(bouquet)) {
    return items > 0 ? items : null;
  }
  return bouquet + items;
}

/** One roll-item card on the Ribbon/Paper tabs: batches aggregated per item. */
export interface RollItemCard {
  itemId: string;
  name: string;
  categoryCode: string;
  categoryLabel: string;
  /** G-93: null until someone photographs the roll; ItemThumb falls back. */
  imageKey: string | null;
  color: { id: string; name: string; hex: string | null } | null;
  availableCm: number;
}

export function aggregateRolls(rolls: PaperRollRow[]): RollItemCard[] {
  const byItem = new Map<string, RollItemCard>();
  for (const r of rolls) {
    const code = r.item.category?.code ?? "PAPER";
    const cm = Number(r.remainingQty);
    const existing = byItem.get(r.item.id);
    if (existing) {
      existing.availableCm += Number.isFinite(cm) ? cm : 0;
    } else {
      byItem.set(r.item.id, {
        itemId: r.item.id,
        name: r.item.name,
        categoryCode: code,
        categoryLabel: r.item.category?.label ?? t("common.paper"),
        imageKey: r.item.imageKey ?? null,
        color: r.item.color ?? null,
        availableCm: Number.isFinite(cm) ? cm : 0,
      });
    }
  }
  return [...byItem.values()].sort((a, b) => compareText(a.name, b.name));
}

export function flowerLineFromCard(it: BouquetBuilderItem): ComposerFlowerLine {
  return {
    itemId: it.itemId,
    name: it.name,
    imageKey: it.imageKey,
    qty: 1,
    maxQty: Number(it.qtyOnHand) || 0,
    unitSalePrice: it.saleUnitPriceEur != null ? Number(it.saleUnitPriceEur) : null,
  };
}

// ── Checkout ──────────────────────────────────────────────────────────────────

/**
 * G-56: how a staff-recorded order arrived. These are all REMOTE channels
 * except MANUAL ("Other"), which mirrors the server's REMOTE_ORDER_SOURCES:
 * a remote customer collecting in the shop must be reachable.
 */
export const STAFF_ORDER_SOURCE_OPTIONS: ReadonlyArray<{
  value: StaffOrderSource;
  /**
   * WhatsApp and Instagram are brands and keep their spelling in every
   * language, so they are words here and keys for the other two. Writing
   * five identical dictionary rows for "WhatsApp" would invite a translator
   * to improve one of them.
   *
   * BOTH ARE OPTIONAL, so read this list through `orderSourceLabel()` and
   * never `option.label` — that renders an EMPTY button for the two that
   * carry a key instead, and it typechecks and builds while doing it.
   */
  label?: string;
  labelKey?: PlainKey;
}> = [
  { value: "PHONE", labelKey: "orderCreate.phone" },
  { value: "WHATSAPP", label: "WhatsApp" },
  { value: "INSTAGRAM", label: "Instagram" },
  { value: "MANUAL", labelKey: "orderBuild.sourceOther" },
];

/** Human label for ANY source, incl. adapter ones (edit mode shows it as text). */
export function orderSourceLabel(source: OrderSource): string {
  const staff = STAFF_ORDER_SOURCE_OPTIONS.find((o) => o.value === source);
  if (staff) return staff.labelKey ? t(staff.labelKey) : (staff.label ?? source);
  return source.charAt(0) + source.slice(1).toLowerCase();
}

export const REMOTE_ORDER_SOURCES: ReadonlySet<StaffOrderSource> = new Set([
  "PHONE",
  "WHATSAPP",
  "INSTAGRAM",
]);

export interface CheckoutDraft {
  /** G-15 / G-56: how the order arrived. */
  source: StaffOrderSource;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  customerNotes: string;
  orderType: "personal" | "business";
  fulfillment: "PICKUP" | "COURIER";
  timeType: "ASAP" | "EXACT" | "WINDOW";
  date: string;
  timeFrom: string;
  timeUntil: string;
  prepaid: boolean;
  /** G-20: deposit actually taken ("" = paid in full when prepaid). */
  prepaidAmountEur: string;
  prepaidMethod: "CASH" | "CARD" | "BANK_TRANSFER";
  // Delivery (COURIER only) — the job carries the address, so these are
  // required whenever fulfillment is COURIER.
  deliveryAddress: string;
  recipientName: string;
  recipientPhone: string;
  recipientNotes: string;
  deliveryPriceEur: string;
  packageSize: "" | PackageSize;
  distanceKm: string;
  /** Manual fallback when the selected courier has no active payout rule. */
  overridePayoutEur: string;
  /** "" = leave the job unassigned (dispatch later from the Dispatch page). */
  courierUserId: string;
}

export function emptyCheckoutDraft(date: string): CheckoutDraft {
  return {
    source: "PHONE",
    customerName: "",
    customerPhone: "",
    customerEmail: "",
    customerNotes: "",
    orderType: "personal",
    fulfillment: "PICKUP",
    timeType: "ASAP",
    date,
    timeFrom: "12:00",
    timeUntil: "14:00",
    prepaid: false,
    prepaidAmountEur: "",
    prepaidMethod: "CARD",
    deliveryAddress: "",
    recipientName: "",
    recipientPhone: "",
    recipientNotes: "",
    deliveryPriceEur: "",
    packageSize: "",
    distanceKm: "",
    overridePayoutEur: "",
    courierUserId: "",
  };
}

/**
 * G-177 (KI-42, KI-43): what saving this page may do to the order's delivery
 * leg — one answer for the form, its checks and the save.
 *
 * - A job a courier already holds keeps its price, distance and courier: the
 *   server locks them once assigned, so they are shown and never re-sent
 *   (re-sending the price was refused, and the page then called the job
 *   failed). Changing them is unassign + assign on the Courier board.
 * - A job that can no longer be edited (cancelled, or past pickup) is left
 *   alone: no update, no cancel, no second job (the server allows one job per
 *   order), and the order keeps its fulfilment — a delivery with no job that
 *   could carry it, or a delivered order turned into a pickup, is never
 *   saved. The form names the job instead.
 * - An external-courier order keeps its fulfilment (it is never sent, so the
 *   server keeps it) and never gets an in-house job or courier from here; a
 *   job it already has is edited like any other (address, recipient, and its
 *   price while no courier holds it).
 */
export interface DeliveryLegPlan {
  /** The fulfilment the save sends; null = leave the order's own. */
  fulfillment: CheckoutDraft["fulfillment"] | null;
  /** Shown instead of the Pickup / Delivery chips. */
  lockedFulfillmentLabel: string | null;
  /** The delivery fields are shown, checked and saved. */
  deliveryDetails: boolean;
  /** Price, distance and courier are read-only (a courier holds the job). */
  courierLocked: boolean;
  /** The order's job can no longer be edited (cancelled, picked up, done). */
  jobClosed: boolean;
  /** Save edits the existing job; when false and `mayCreateJob`, it creates one. */
  updateJob: boolean;
  mayCreateJob: boolean;
  /** The courier picker is offered and an assignment sent. */
  mayAssignCourier: boolean;
  /** Switched to pickup: the order's open job is cancelled. */
  mayCancelJob: boolean;
}

export function deliveryLegPlan(
  editing: Pick<CustomerOrder, "fulfillment" | "deliveryJob"> | null,
  draftFulfillment: CheckoutDraft["fulfillment"],
): DeliveryLegPlan {
  const job = editing?.deliveryJob ?? null;
  // The server edits a job only while it waits for a courier (PENDING) or
  // has one (ASSIGNED); price and distance only while PENDING.
  const jobOpen = job != null && (job.status === "PENDING" || job.status === "ASSIGNED");
  const jobClosed = job != null && !jobOpen;
  const courierLocked = job?.status === "ASSIGNED";
  if (editing && (editing.fulfillment === "EXTERNAL_COURIER" || jobClosed)) {
    return {
      fulfillment: null,
      lockedFulfillmentLabel: fulfillmentLabel(t, editing.fulfillment),
      deliveryDetails: jobOpen,
      courierLocked,
      jobClosed,
      updateJob: jobOpen,
      mayCreateJob: false,
      mayAssignCourier: false,
      mayCancelJob: false,
    };
  }
  const courier = draftFulfillment === "COURIER";
  return {
    fulfillment: draftFulfillment,
    lockedFulfillmentLabel: null,
    deliveryDetails: courier,
    courierLocked: courier && courierLocked,
    jobClosed: false,
    updateJob: courier && jobOpen,
    mayCreateJob: courier && job == null,
    mayAssignCourier: courier && !courierLocked,
    mayCancelJob: !courier && jobOpen,
  };
}

/**
 * Returns the first blocking problem, or null when the draft can be sent.
 *
 * The MODULE `t` here, not a threaded one, and deliberately: these sentences
 * are produced by a click and put into state. A message about what happened
 * when the florist pressed Send is a record of that moment — it should not
 * quietly restate itself in another language afterwards. `fulfillmentLabel`
 * in `orderUi.ts` is the opposite case and takes `t` for that reason.
 */
export function validateCheckout(
  draft: CheckoutDraft,
  // G-177: an external-courier order with no job has no delivery fields.
  deliveryDetails: boolean = draft.fulfillment === "COURIER",
): string | null {
  if (!draft.customerName.trim()) return t("orderBuild.customerNameRequired");
  if (draft.fulfillment === "PICKUP" && REMOTE_ORDER_SOURCES.has(draft.source)) {
    // Mirrors the server rule: a remote source + PICKUP needs reachable
    // contact; an "Other" (MANUAL) customer may be standing in the shop.
    if (!draft.customerPhone.trim())
      return t("orderBuild.pickupNeedsPhone");
    if (!draft.customerEmail.trim())
      return t("orderBuild.pickupNeedsEmail");
  }
  if (draft.timeType !== "ASAP" && !draft.timeFrom)
    return t("orderBuild.pickTime");
  if (draft.timeType === "WINDOW" && !draft.timeUntil)
    return t("orderBuild.pickWindowEnd");
  if (deliveryDetails) {
    if (!draft.deliveryAddress.trim()) return t("orderBuild.addressRequired");
    const price = Number(draft.deliveryPriceEur);
    if (!draft.deliveryPriceEur.trim() || !Number.isFinite(price) || price < 0)
      return t("orderBuild.deliveryPriceRequired");
  }
  return null;
}

// ── Stock shortages (API error payload of POST /customer-orders) ─────────────

export interface StockShortage {
  itemId: string;
  name: string;
  requested: string;
  available: string;
}

/**
 * Pull the structured shortage list out of a failed request. The API error
 * body is `{ message: "Location does not have enough stock", code:
 * "STOCK_SHORTAGE", details: { shortages: [...] } }` — KI-9: the list rides
 * under `details`, the one key the server's error envelope passes through.
 */
export function parseShortages(err: unknown): StockShortage[] | null {
  if (!err || typeof err !== "object") return null;
  const body = (err as { body?: unknown }).body;
  if (!body || typeof body !== "object") return null;
  const details = (body as { details?: unknown }).details;
  if (!details || typeof details !== "object") return null;
  const shortages = (details as { shortages?: unknown }).shortages;
  if (!Array.isArray(shortages) || shortages.length === 0) return null;
  return shortages.filter(
    (s): s is StockShortage =>
      !!s &&
      typeof s === "object" &&
      typeof (s as StockShortage).itemId === "string" &&
      typeof (s as StockShortage).name === "string",
  );
}
